import React, { useEffect, useMemo, useState } from 'react';
import Container from '@common/components/container';

import { TopNavigationBar } from '@common/components/navigation-bar';
import { useTranslation } from 'react-i18next';
import { Button } from '@common/components/button';
import { Route, useHistory, useRouteMatch } from 'react-router';
import { Switch } from '@common/components/router';
import AllCompetancy from './all-compentancy';
import ArchivedCompetancy from './archived-competancy';
import { useRequest } from '@common/services/api';
import { selected } from '@modules/organisation/selectors/organisation';
import { useAppSelector, useDidComponentMount } from '@common/hooks';
import { EPredicateFilters } from '@common/components/predicates-filter';
import { EPredicateFields } from '@common/definitions';
import { Predicates } from '@modules/performance/types';
import './styles.scss';

const LibraryContainer = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const match = useRouteMatch();
  const organisation = useAppSelector(selected);
  const { networkId } = match.params as { networkId: string };
  const basePath = networkId ? `/networks/${networkId}` : '/admin';
  const baseUrl = networkId ? '/networks/:id' : '/admin';
  const [search, setSearch] = useState<string>('');
  const didMount = useDidComponentMount();
  // const languages = useAppSelector((state) => state.language.available);
  // const defaultLanguage = useAppSelector((state) => state.loggedUser.user.language_locale);

  const categoryFilterOptions = [
    { label: t('performance:category'), value: 'category' },
    {
      label: t('performance:function'),
      value: 'function',
    },
  ];

  const [predicates, setPredicates] = useState([
    {
      filter: EPredicateFilters[EPredicateFields.COMPETENCY_GROUP_BY],
      value: {
        attribute: EPredicateFields.COMPETENCY_GROUP_BY,
        comparison: 'eq' as const,
        value: 'category',
      },
      options: categoryFilterOptions,
    },
    // {
    //   filter: EPredicateFilters[EPredicateFields.COMPETENCY_LANGUAGE],
    //   value: {
    //     attribute: EPredicateFields.COMPETENCY_LANGUAGE,
    //     comparison: 'eq' as const,
    //     value: defaultLanguage,
    //   },
    //   options: languages || [],
    // },
  ]);

  const [group_by, language] = useMemo(() => {
    return [
      predicates.find(
        (p) => p.filter === EPredicateFilters[EPredicateFields.COMPETENCY_GROUP_BY]
      )?.value.value,
      predicates.find(
        (p) => p.filter === EPredicateFilters[EPredicateFields.COMPETENCY_LANGUAGE]
      )?.value.value,
    ];
  }, [predicates]);

  const payload = useMemo(
    () => ({
      ...(group_by && group_by !== '' ? { groupBy: group_by } : {}),
      ...(search && search !== '' ? { q: search } : {}),
    }),
    [group_by, search]
  );

  const apiConfig = {
    payload,
    // language: language || '',
    transform: (res: any) => {
      return {
        ...res,
        data: res.data.map((item: any) => {
          const isGroupedByFunction = !!item.function;
          const isGroupedByCategory = !!item.category;

          if (isGroupedByFunction) {
            return {
              groupType: 'function',
              group: {
                id: item.function.id,
                name: item.function.name,
              },
              competencies: item.competencies.map((competency: any) => ({
                ...competency,
                name: competency.name?.string
                  ? competency.name.string
                  : typeof competency.name === 'object' &&
                    competency.name !== null
                  ? language && competency.name[language]
                    ? competency.name[language]
                    : Object.values(competency.name)[0] || null
                  : competency.name || null,
                created_by: res.meta.related.users.find(
                  (user: any) => user.id === competency.created_by
                ),
              })),
            };
          }
          if (isGroupedByCategory) {
            return {
              groupType: 'category',
              group: {
                id: item.category.id,
                name: item.category.name?.string
                  ? {
                      string: item.category.name.string,
                    }
                  : typeof item.category.name === 'object' &&
                    item.category.name !== null
                  ? {
                      string:
                        language && item.category.name[language]
                          ? item.category.name[language]
                          : Object.values(item.category.name)[0] ||
                            null,
                    }
                  : item.category.name,
              },
              competencies: item.competencies.map((competency: any) => ({
                ...competency,
                name: competency.name?.string
                  ? competency.name.string
                  : typeof competency.name === 'object' &&
                    competency.name !== null
                  ? language && competency.name[language]
                    ? competency.name[language]
                    : Object.values(competency.name)[0] || null
                  : competency.name || null,
                created_by: res.meta.related.users.find(
                  (user: any) => user.id === competency.created_by
                ),
              })),
            };
          }

          return {
            groupType: 'category',
            group: {
              id: item.category?.id || '',
              name: item.category?.name?.string
                ? {
                    string: item.category.name.string,
                  }
                : typeof item.category?.name === 'object' &&
                  item.category?.name !== null
                ? {
                    string:
                      language && item.category.name[language]
                        ? item.category.name[language]
                        : Object.values(item.category.name)[0] ||
                          null,
                  }
                : item.category?.name || null,
            },
            competencies: item.competencies.map((competency: any) => ({
              ...competency,
              name: competency.name?.string
                ? competency.name.string
                : typeof competency.name === 'object' &&
                  competency.name !== null
                ? language && competency.name[language]
                  ? competency.name[language]
                  : Object.values(competency.name)[0] || null
                : competency.name || null,
              created_by: res.meta.related.users.find(
                (user: any) => user.id === competency.created_by
              ),
            })),
          };
        }),
      };
    },
  };

  const {
    data: competency,
    setData: setCompetency,
    loading,
    reset,
    refetch
  } = useRequest(
    `/v1/organisations/${organisation.id}/performance/competencies`,
    apiConfig
  );

  useEffect(() => {
    if (!didMount) return;
    reset();
  }, [payload, didMount, reset]);

  return (
    <Container name="CompetencyLibrary">
      <TopNavigationBar
        title={t('performance:competency_library')}
        action={
          <div className="tw-flex tw-items-center tw-gap-2">
            <Button
              type="default"
              size="large"
              onClick={() => {
                history.push(
                  `${basePath}/performance/competency-library/manage-rating-scale`
                );
              }}
            >
              {t('performance:manage_rating_scale')}
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={() => {
                history.push(
                  `${basePath}/performance/competency-library/new-competency`
                );
              }}
            >
              {t('performance:new_competency')}
            </Button>
          </div>
        }
        tabs={[
          {
            name: t('performance:all_competencies'),
            to: `${basePath}/performance/competency-library/all`,
          },
          {
            name: t('performance:archived_competencies'),
            to: `${basePath}/performance/competency-library/archived`,
          },
        ]}
      />
      <Switch>
        <Route exact path={`${baseUrl}/performance/competency-library/all`}>
          <AllCompetancy
            loading={loading}
            competency={competency}
            setCompetency={setCompetency}
            predicates={predicates as Predicates}
            setPredicates={setPredicates}
            setSearch={setSearch}
            refetch={refetch}
          />
        </Route>
        <Route
          exact
          path={`${baseUrl}/performance/competency-library/archived`}
        >
          <ArchivedCompetancy />
        </Route>
      </Switch>
    </Container>
  );
};

export default LibraryContainer;
